/* ============================================================================
   THE PDP GALLERY RAIL. 9 September 2026.

   assets/rn-pdp-fit.css turns the horizontal thumbnail strip into a vertical
   rail beside a large stage, and pins the whole media column while the buy box
   scrolls past. This file does the two things CSS cannot: it wraps the rail so
   it can carry up/down controls, and it keeps those controls honest.

   ============================================================================
   WHAT THIS REPLACED, SO IT IS NOT REBUILT BY MISTAKE

   The first two attempts at this were McGee's scrolling stack — one frame per
   row, then two-up. Sharon rejected both, and the reasons are in the header of
   rn-pdp-fit.css. The short version: our buy box is about 2,000px tall, a
   stacked gallery is taller still, and whichever finishes first leaves a dead
   column beside the other. Stacking also pushes the photographs away from the
   swatches that change them.

   Design Within Reach's arrangement does not have those problems, because the
   media column stays one screen tall and can therefore be pinned.

   ============================================================================
   THREE RULES THIS FILE FOLLOWS

   1. IT MUST NOT ADD, REMOVE OR REORDER A .bb-th. The buy box resolves which
      photograph was clicked with childIndex(thumbsEl, t) — the element's
      position among its parent's children. Add one node and every index after
      it shifts, and clicks start selecting the wrong photograph. The wrapper
      goes AROUND .bb-thumbs; the buttons are siblings of .bb-thumbs inside that
      wrapper, never children of it.

   2. MOVING .bb-thumbs INTO A WRAPPER IS SAFE, and was checked rather than
      assumed. The section captured `thumbsEl` as a node reference, not a
      selector, so re-parenting does not break it; its click handler tests
      `t.parentNode === thumbsEl`, which is unaffected; and no rule in the
      section or in any stylesheet targets .bb-thumbs by way of its parent.

   3. IT FAILS OPEN. Every branch that cannot work leaves the gallery exactly as
      the section rendered it. This runs on the page that takes the money.
   ============================================================================ */
(function () {
  'use strict';

  /* ==========================================================================
     MEASURE THE HEADER. DO NOT DERIVE IT.

     The pinned media column has to sit directly under the sticky header stack —
     the announcement marquee, the nav bar and the countdown ribbon. The theme
     publishes two custom properties for that, and the buy box pins at their sum:

         --header-height        the nav bar
         --header-group-height  the rest of #header-group

     Both are computed by an inline script in layout/theme.liquid that walks
     #header-group.children and skips the one that IS <header-component>:

         if (element === header || !(element instanceof HTMLElement)) continue;

     That comparison only holds if <header-component> is a DIRECT child of
     #header-group. Section groups wrap each section in its own div, so the
     identity test can silently never match — and whichever way it lands, one of
     the two numbers is wrong: either the bar is counted twice or it is missed
     entirely. Pinning at the sum then puts the photograph too low or, as
     Sharon kept seeing, too high with its top sliding under the bar:
     "as I scroll it still does that."

     Two attempts at reasoning about which of the two variables to trust both
     produced the same complaint, so this stops deriving the number and reads
     the rendered height of #header-group itself. One measurement, no
     assumptions about the theme's DOM shape, and it self-corrects if the
     ribbon is ever removed or a second bar is added.

     Published on <html> so any stylesheet can use it. rn-pdp-fit.css consumes
     it with the old expression as a fallback, so if this never runs the page
     behaves exactly as it did before rather than losing the pin altogether.
     ========================================================================== */
  function measureHeader() {
    try {
      var hg = document.getElementById('header-group');
      if (!hg) return;
      var h = Math.round(hg.getBoundingClientRect().height);
      /* A collapsed or not-yet-laid-out header would pin the column at the very
         top of the window and hide it behind the bar — worse than the fallback. */
      if (h > 0) document.documentElement.style.setProperty('--rn-hdr-real', h + 'px');
    } catch (e) {}
  }

  function watchHeader() {
    measureHeader();
    /* Webfonts and the countdown script can both change the bar's height after
       first paint, so this is taken again once things settle. */
    window.addEventListener('load', measureHeader);
    window.addEventListener('resize', measureHeader);
    setTimeout(measureHeader, 300);
    setTimeout(measureHeader, 1200);
    try {
      var hg = document.getElementById('header-group');
      if (hg && window.ResizeObserver) new ResizeObserver(measureHeader).observe(hg);
    } catch (e) {}
  }

  function build(root) {
    var thumbs = root.querySelector('.bb-thumbs');
    if (!thumbs || thumbs.parentNode && thumbs.parentNode.classList &&
        thumbs.parentNode.classList.contains('rn-rail')) return null;
    if (!thumbs.parentNode) return null;

    var rail = document.createElement('div');
    rail.className = 'rn-rail';

    var up = document.createElement('button');
    up.type = 'button';
    up.className = 'rn-rail-btn rn-rail-up';
    up.setAttribute('aria-label', 'Previous photographs');
    up.textContent = '↑';

    var down = document.createElement('button');
    down.type = 'button';
    down.className = 'rn-rail-btn rn-rail-down';
    down.setAttribute('aria-label', 'More photographs');
    down.textContent = '↓';

    thumbs.parentNode.insertBefore(rail, thumbs);
    rail.appendChild(up);
    rail.appendChild(thumbs);   /* moves it — see rule 2 */
    rail.appendChild(down);

    function step() {
      var first = thumbs.querySelector('.bb-th');
      if (!first) return 120;
      var r = first.getBoundingClientRect();
      var gap = parseFloat(getComputedStyle(thumbs).rowGap || getComputedStyle(thumbs).gap || 8) || 8;
      /* Two frames a press: one feels broken on a rail this small, a whole
         page loses the reader's place. */
      return (r.height + gap) * 2;
    }

    function sync() {
      try {
        var over = thumbs.scrollHeight - thumbs.clientHeight;
        /* Everything already fits: the arrows would be two dead controls, so
           the CSS hides them entirely rather than showing them greyed out. */
        rail.classList.toggle('is-static', over <= 2);
        up.disabled = thumbs.scrollTop <= 2;
        down.disabled = thumbs.scrollTop >= over - 2;
      } catch (e) {}
    }

    up.addEventListener('click', function () { thumbs.scrollBy({ top: -step(), behavior: 'smooth' }); });
    down.addEventListener('click', function () { thumbs.scrollBy({ top: step(), behavior: 'smooth' }); });
    thumbs.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    return { rail: rail, thumbs: thumbs, sync: sync };
  }

  function boot(root) {
    if (!root || root.__rnRailBound) return;
    root.__rnRailBound = true;

    try {
      var made = build(root);
      if (!made) return;

      made.sync();

      /* The section rebuilds .bb-thumbs when the colourway, size or
         configuration changes, and re-marks which thumbnail is active. Both
         change whether the rail overflows and where it is scrolled to, so the
         controls are re-checked after either. */
      if (window.MutationObserver) {
        var mo = new MutationObserver(function () {
          clearTimeout(root.__rnRailT);
          root.__rnRailT = setTimeout(function () {
            made.sync();
            /* Keep the active frame in the rail's view. The section already
               calls scrollIntoView on it, but that ran when the rail was a
               horizontal strip; on a vertical one the block axis is what
               matters. */
            try {
              var on = made.thumbs.querySelector('.bb-th.on');
              if (on && on.scrollIntoView) on.scrollIntoView({ block: 'nearest' });
            } catch (e) {}
          }, 60);
        });
        mo.observe(made.thumbs, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      }
    } catch (e) {
      /* fail open */
    }
  }

  function run() {
    watchHeader();
    [].forEach.call(document.querySelectorAll('[id^="rn-bb-"]'), boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
  document.addEventListener('shopify:section:load', function () { setTimeout(run, 0); });
})();
